/**
 * How a URL names a forecast map config (`?config=`): by its uuid. The API
 * stays keyed on the integer id; the uuid is only the public reference, so
 * map links do not expose (or invite guessing) sequential ids.
 *
 * Links written before the uuid existed carry the integer id, and still
 * resolve: a ref is either a lower-case canonical uuid or the decimal form of
 * a positive integer id.
 */
import type {ForecastMapConfig} from '../../api/types';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** A `?config=` value, or null when it names no config. */
export const parseForecastConfigRef = (raw: string | null): string | null => {
  if (raw === null) {
    return null;
  }
  const lowered = raw.toLowerCase();
  if (UUID.test(lowered)) {
    return lowered;
  }
  // A legacy id: positive integers only, as `?config=` always read.
  const id = Number(raw);
  return raw !== '' && Number.isInteger(id) && id > 0 ? id.toString() : null;
};

/** The ref a URL writes for `config`. */
export const forecastConfigRef = (
  config: Pick<ForecastMapConfig, 'uuid'>,
): string => config.uuid;

/** Whether `ref` (a parsed `?config=`) names `config`. */
export const matchesForecastConfigRef = (
  config: Pick<ForecastMapConfig, 'id' | 'uuid'>,
  ref: string | null,
): boolean =>
  ref !== null &&
  (ref === config.uuid || (!ref.includes('-') && ref === config.id.toString()));
